import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { debounce, isEqual } from 'lodash';

import { useDispatch, useSelector } from 'react-redux';

import { changeDealData, hidePlannerSecondaryPanel } from 'store/dealManagement/reducer';

import PlannerSecondaryPanel from 'components/pages/Planner/PlannerSecondaryPanel';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import SelectOption from 'lib/SelectOption';
import Search from 'lib/Search';

import { CodeNameModel } from 'components/common/types';
import { Store } from 'components/common/types/Store.types';
import { CodeName } from 'components/common/types/CodeName.types';
import { getAssetLookup } from 'modules/api/lookups';
import { cancelFunctions } from 'utils/cancelFunctions';
import { changeAdvertiserOptions } from 'store/userOptions/reducer';
import Button, { Color } from 'lib/Button';
import SortButton from 'lib/SortButton';

const DuplicationPanel = (): React.ReactElement => {
  const dispatch = useDispatch();

  const organisations = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions.organisations);
  const productCategories = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions.productCategories);
  const organisationOptions = useSelector((state: Store) => state.userOptions.advertiserOptions);

  const [tmpOrganisations, setTmpOrganisations] = useState<CodeName[]>(organisations);
  const [message, setMessage] = useState('');

  const advertiserOptionsWithCategory = useMemo(() => {
    return { advertisers: organisationOptions.map((organisation) => ({ ...organisation, category: 'advertisers' })) };
  }, [organisationOptions]);

  const organisationsWithGroup = useMemo(() => {
    return tmpOrganisations.map((organisation) => ({ ...organisation, category: 'advertisers' }));
  }, [tmpOrganisations]);

  const {
    searchText,
    selectedOptions: selectedOrganisations,
    onCategoryDirectionSort,
    filteredOptions,
    sortDirection,
    isOptionSelected,
    onFilterTextChange: changeSearchText,
    onOptionIncludeExcludeChange,
    setSelectedOptions,
  } = useCategoryOptions({
    options: advertiserOptionsWithCategory,
    initialSelectedOptions: organisationsWithGroup,
    isServerSideFiltering: true,
  });

  const getAdvertisers = useMemo(
    () =>
      debounce(async (query: string): Promise<void> => {
        try {
          const advertisersLookup = await getAssetLookup({ cancelFunctions, lookupURLName: 'advertiser', query });
          if (advertisersLookup && advertisersLookup.length) {
            dispatch(changeAdvertiserOptions(advertisersLookup.map(({ code, name }: CodeName) => ({ code, name }))));
          } else {
            setMessage('There are no results matching your search');
          }
        } catch (error) {
          if (axios.isCancel(error)) return;
          // eslint-disable-next-line no-console
          console.warn(`Error fetching meta data: ${error}`);
        }
      }, 300),
    [dispatch],
  );

  useEffect(() => {
    return () => {
      getAdvertisers.cancel();
      dispatch(changeAdvertiserOptions([]));
    };
  }, []);

  const isChange = useMemo(
    () => !isEqual(organisationsWithGroup, selectedOrganisations),
    [organisationsWithGroup, selectedOrganisations],
  );

  const onSubmit = (): void => {
    const updatedOrganisations = selectedOrganisations.map(({ code, name }) => ({ code, name }));
    dispatch(changeDealData({ exclusions: { organisations: updatedOrganisations, productCategories } }));
    dispatch(hidePlannerSecondaryPanel());
  };

  useEffect(() => {
    setTmpOrganisations(organisations);
  }, [organisations]);

  useEffect(() => {
    setTmpOrganisations(selectedOrganisations);
  }, [isChange]);

  const onChange = (value: string): void => {
    dispatch(changeAdvertiserOptions([]));
    setMessage('');
    changeSearchText(value);

    if (!value.length) getAdvertisers.cancel();
    else getAdvertisers(value);
  };

  return (
    <PlannerSecondaryPanel
      size="lg"
      title="Add duplication advertisers"
      customStyles={{ left: '430px' }}
      isLineLevel={false}
      confirmButtonLabel="Add"
      cancelButtonLabel="Back"
      showBackButton
      isChange={isChange}
      onSubmit={onSubmit}
      onCancel={() => dispatch(hidePlannerSecondaryPanel())}
    >
      <div className="mb-2">
        <Search value={searchText} placeholder="Search advertisers" onChange={onChange} />
      </div>
      {canDisplayOptionResults(filteredOptions) && (
        <div className="flex justify-between">
          <SortButton sortDir={sortDirection} onSort={onCategoryDirectionSort} dataTestId="collapsible-list-sort" />
          <Button color={Color.TRANSPARENT} label="Clear all" onClick={() => setSelectedOptions([])} />
        </div>
      )}
      {canDisplayOptionResults(filteredOptions) &&
        filteredOptions.advertisers.map(({ code, name, category }) => {
          return (
            <SelectOption
              value={{ code, name }}
              key={`select-category-${code}`}
              isSelected={isOptionSelected(code, 'advertisers')}
              isDisabled={false}
              isMultiple
              hasCheckbox
              isIncludeExcludeAlwaysVisible
              classNames="!m-0"
              onClick={(value: CodeNameModel) =>
                onOptionIncludeExcludeChange({
                  ...value,
                  category,
                })
              }
            >
              {name}
            </SelectOption>
          );
        })}
      {message && <p>{message}</p>}
    </PlannerSecondaryPanel>
  );
};

export default DuplicationPanel;
